import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getUserOrders, ORDER_STATUS } from '../../firebase/orders';
import { addCartItem } from '../../firebase/Cart';
import PageLoader from '../PageLoader/PageLoader';
import usePageLoader from '../../hooks/usePageLoader';
import './PurchaseHistory.css';

// ─── Status config ────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
    'pending':                           { bg:'#fff8e1', text:'#b07c00', label:'Pending Approval' },
    [ORDER_STATUS.PENDING_APPROVAL]:     { bg:'#fff8e1', text:'#b07c00', label:'Pending Approval' },
    [ORDER_STATUS.APPROVED]:             { bg:'#e3f0ff', text:'#1a5faa', label:'Approved — Proceed to Checkout' },
    [ORDER_STATUS.AWAITING_PAYMENT]:     { bg:'#f3e5f5', text:'#7b1fa2', label:'Awaiting Payment Confirmation' },
    [ORDER_STATUS.IN_PRODUCTION]:        { bg:'#e6f4ea', text:'#2d7a3a', label:'In Production' },
    'in production':                     { bg:'#e6f4ea', text:'#2d7a3a', label:'In Production' },
    'processing':                        { bg:'#e6f4ea', text:'#2d7a3a', label:'In Production' },
    [ORDER_STATUS.TO_BE_DELIVERED]:      { bg:'#fff3e0', text:'#e65100', label:'Ready for Pickup / Delivery' },
    [ORDER_STATUS.DELIVERED]:            { bg:'#e8f5e9', text:'#1b5e20', label:'Delivered' },
    'completed':                         { bg:'#e8f5e9', text:'#1b5e20', label:'Delivered' },
    [ORDER_STATUS.CANCELLED]:            { bg:'#fdecea', text:'#b71c1c', label:'Cancelled' },
};

const STATUS_STEPS = [
    { key:[ORDER_STATUS.PENDING_APPROVAL, 'pending'], label:'Order Submitted' },
    { key:[ORDER_STATUS.APPROVED],                    label:'Approved' },
    { key:[ORDER_STATUS.AWAITING_PAYMENT],            label:'Payment Verification' },
    { key:[ORDER_STATUS.IN_PRODUCTION, 'in production', 'processing'], label:'In Production' },
    { key:[ORDER_STATUS.TO_BE_DELIVERED],             label:'Ready for Pickup' },
    { key:[ORDER_STATUS.DELIVERED, 'completed'],      label:'Delivered' },
];

function getStepIndex(status) {
    return STATUS_STEPS.findIndex(step => step.key.includes(status));
}

const TABS = [
    { label:'All Orders',        match:null },
    { label:'Pending Approval',  match:['pending', ORDER_STATUS.PENDING_APPROVAL] },
    { label:'Approved',          match:[ORDER_STATUS.APPROVED] },
    { label:'Awaiting Payment',  match:[ORDER_STATUS.AWAITING_PAYMENT] },
    { label:'In Production',     match:[ORDER_STATUS.IN_PRODUCTION, 'in production', 'processing'] },
    { label:'To Be Delivered',   match:[ORDER_STATUS.TO_BE_DELIVERED] },
    { label:'Delivered',         match:[ORDER_STATUS.DELIVERED, 'completed'] },
    { label:'Cancelled',         match:[ORDER_STATUS.CANCELLED] },
];

// Subcategories that use per-size pricing with no color or print dimensions
const NO_COLOR_SUBCATEGORIES = ['Polo Shirt', 'Jersey'];
const CLOTHING_SUBCATEGORIES_PH = ['T-Shirt', 'Polo Shirt', 'Jersey'];
function isGenericProduct(item) {
    const sub = item?.subcategory;
    if (!sub) return false;
    return !CLOTHING_SUBCATEGORIES_PH.includes(sub);
}

function formatDate(value) {
    if (!value) return '—';
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' });
}

function buildOrderTitle(orderItems = []) {
    if (!orderItems.length) return 'Order';
    const types = [...new Set(orderItems.map(item => item.type || item.name).filter(Boolean))];
    return types[0] || 'Order';
}

// ─── Size display map ─────────────────────────────────────────────────────────
const SIZE_KEY_TO_DISPLAY = {
    xs:'XS', small:'Small', medium:'Medium', large:'Large',
    xl:'XL', xxl:'2XL', xxxl:'3XL', xxxxl:'4XL', xxxxxl:'5XL',
    XXS:'XXS', XS:'XS', Small:'Small', Medium:'Medium', Large:'Large',
    XL:'XL', '2XL':'2XL', '3XL':'3XL', '4XL':'4XL', '5XL':'5XL', '6XL':'6XL',
};

const SHIRT_SIZE_PRICES = {
    'Active Life White T-Shirt':        { xs:120, small:120, medium:120, large:120, xl:125, xxl:130, xxxl:135, xxxxl:null, xxxxxl:140 },
    'Active Life Colored T-Shirt':      { xs:125, small:125, medium:125, large:125, xl:130, xxl:135, xxxl:140, xxxxl:null, xxxxxl:150 },
    'Blue Corner White T-Shirt':        { xs:150, small:150, medium:150, large:160, xl:165, xxl:170, xxxl:175, xxxxl:180, xxxxxl:200 },
    'Blue Corner Colored T-Shirt':      { xs:180, small:180, medium:180, large:195, xl:195, xxl:205, xxxl:210, xxxxl:220, xxxxxl:235 },
    'Yalex White T-Shirt':              { xs:120, small:120, medium:120, large:120, xl:125, xxl:130, xxxl:135, xxxxl:null, xxxxxl:140 },
    'Yalex Colored T-Shirt':            { xs:125, small:125, medium:125, large:125, xl:130, xxl:135, xxxl:140, xxxxl:null, xxxxxl:150 },
    'Drift White T-Shirt':              { xs:85,  small:85,  medium:85,  large:85,  xl:90,  xxl:95,  xxxl:null, xxxxl:null, xxxxxl:null },
    'Drift Colored T-Shirt':            { xs:95,  small:95,  medium:95,  large:95,  xl:105, xxl:115, xxxl:null, xxxxl:null, xxxxxl:null },
    'Blackhorse White T-Shirt':         { xs:120, small:120, medium:120, large:120, xl:125, xxl:130, xxxl:null, xxxxl:null, xxxxxl:null },
    'Blackhorse Colored T-Shirt':       { xs:125, small:125, medium:125, large:125, xl:130, xxl:135, xxxl:null, xxxxl:null, xxxxxl:null },
    'Pro Club Inspire White T-Shirt':   { xs:null, small:200, medium:200, large:200, xl:220, xxl:230, xxxl:null, xxxxl:null, xxxxxl:null },
    'Pro Club Inspire Colored T-Shirt': { xs:null, small:250, medium:250, large:250, xl:260, xxl:270, xxxl:null, xxxxl:null, xxxxxl:null },
};

const IMPRINT_RECT = {
    'Full Front':150,'Medium Front':120,'Center Chest':50,'Across Chest':80,
    'Left Chest':50,'Right Chest':50,'Left Vertical':120,'Right Vertical':120,
    'Bottom Left':50,'Bottom Right':50,'Full Back':150,'Medium Back':120,
    'Locker Center Patch':50,'Across Shoulder':80,'Left Sleeve':40,'Right Sleeve':40,
};
const IMPRINT_CIRC = {
    'Full Front':120,'Medium Front':80,'Center Chest':50,'Across Chest':80,
    'Left Chest':40,'Right Chest':40,'Left Vertical':100,'Right Vertical':100,
    'Bottom Left':50,'Bottom Right':50,'Full Back':120,'Medium Back':80,
    'Locker Center Patch':50,'Back Center':80,'Left Sleeve':40,'Right Sleeve':40,
};

function getCaseInsensitivePrice(sizePrices, sz, fallbackPrice) {
    if (!sizePrices) return Number(fallbackPrice) || 0;
    const lowerSz = (sz || '').toLowerCase();
    const matchedKey = Object.keys(sizePrices).find(
        (key) => (key || '').toLowerCase() === lowerSz
    );
    return matchedKey ? (Number(sizePrices[matchedKey]) || Number(fallbackPrice) || 0) : (Number(fallbackPrice) || 0);
}



/**
 * Calculate the correct total for a single order item.
 * Handles T-Shirt (with color surcharge) and Polo/Jersey (sizePrices).
 */
function calcItemTotal(item) {
    // Generic / non-clothing
    if (isGenericProduct(item)) {
        return (Number(item.quantity) || 0) * (Number(item.price) || 0);
    }

    const quantities = item.quantities || {};
    let totalQty   = Object.values(quantities).reduce((s, q) => s + (q || 0), 0);
    if (totalQty === 0) totalQty = Number(item.quantity) || 0;
    if (!totalQty) return 0;

    // Polo Shirt / Jersey
    if (NO_COLOR_SUBCATEGORIES.includes(item.subcategory)) {
        const sizePrices = item.sizePrices || {};
        if (Object.keys(sizePrices).length > 0 && Object.keys(quantities).length > 0) {
            const calculatedTotal = Object.entries(quantities).reduce(
                (s, [size, q]) => s + (q || 0) * getCaseInsensitivePrice(sizePrices, size, item.price), 0
            );
            if (calculatedTotal > 0) return calculatedTotal;
        }

        if (Number(item.itemTotal) > 0) return Number(item.itemTotal);
        return totalQty * (Number(item.price) || 0);
    }

    // T-Shirt: include color surcharge per piece
    const sizePrices     = item.sizePrices && Object.keys(item.sizePrices).length > 0
        ? item.sizePrices
        : (SHIRT_SIZE_PRICES[item.type] || {});
    const colorSurcharge = Number(item.colorSurcharge || 0);
    
    let shirtSubtotal = 0;
    if (Object.keys(quantities).length > 0) {
        shirtSubtotal = Object.entries(quantities).reduce(
            (s, [sz, q]) => s + (q || 0) * (getCaseInsensitivePrice(sizePrices, sz, item.price) + colorSurcharge), 0
        );
    } else {
        shirtSubtotal = totalQty * ((Number(item.price) || 0) + colorSurcharge);
    }

    const shape        = (item.designShape || '').toLowerCase() === 'circle' ? 'circle' : 'rectangle';
    const table        = shape === 'circle' ? IMPRINT_CIRC : IMPRINT_RECT;
    const locs         = (item.printLocation || '').split(',').map(s => s.trim()).filter(Boolean);
    const prices       = locs.map(l => table[l] || 0).sort((a, b) => b - a);
    const imprintPerPc = prices.reduce((s, p, i) => s + (i === 0 ? p : Math.round(p * 0.5)), 0);
    return shirtSubtotal + imprintPerPc * totalQty;
}

/** Compute the final grand total for an order, respecting saved discounts. */
function getOrderGrandTotal(order) {
    const items = order.orderItems || [];
    if (items.length > 0) {
        const subtotal       = items.reduce((s, item) => s + calcItemTotal(item), 0);
        const deliveryFee    = Number(order.deliveryFee    || 0);
        const discountAmount = Number(order.discountAmount || 0);
        return Math.max(0, subtotal + deliveryFee - discountAmount);
    }
    const subtotal       = order.subtotal || 0;
    const discountAmount = order.discountAmount || 0;
    return Math.max(0, subtotal - discountAmount);
}

/** Build a per-size breakdown string from quantities map */
function buildSizeBreakdown(quantities) {
    if (!quantities || Object.keys(quantities).length === 0) return null;
    const entries = Object.entries(quantities).filter(([, q]) => q > 0);
    if (entries.length === 0) return null;
    return entries
        .map(([size, qty]) => `${SIZE_KEY_TO_DISPLAY[size] || size}: ${qty}`)
        .join(', ');
}

// ─── Component ────────────────────────────────────────────────────────────────
function PurchaseHistory() {
    const navigate        = useNavigate();
    const { currentUser } = useAuth();
    const location        = useLocation();

    const [activeTab, setActiveTab] = useState(() => {
        if (location.state?.tab === 'cancelled') return TABS.findIndex(t => t.label === 'Cancelled');
        return 0;
    });
    const [orders,            setOrders]            = useState([]);
    const [loading,           setLoading]           = useState(true);
    const [expandedId,        setExpandedId]        = useState(null);
    const [orderAgainLoading, setOrderAgainLoading] = useState(null);
    const [orderAgainMsg,     setOrderAgainMsg]     = useState({});

    const { pageReady, markPageReady } = usePageLoader();

    useEffect(() => {
        if (!currentUser) return;
        const load = async () => {
            setLoading(true);
            const { orders: fetched } = await getUserOrders(currentUser.uid);
            setOrders(fetched || []);
            setLoading(false);
            markPageReady();
        };
        load();
    }, [currentUser]);

    const tab      = TABS[activeTab];
    const filtered = tab.match === null ? orders : orders.filter(o => tab.match.includes(o.status));
    const countByTab = (t) => t.match === null ? orders.length : orders.filter(o => t.match.includes(o.status)).length;

    async function handleOrderAgain(order) {
        if (!currentUser) return;
        const orderItems = order?.orderItems || [];
        if (!orderItems.length) {
            setOrderAgainMsg(prev => ({ ...prev, [order.firestoreId]: { type:'error', text:'No items found for this order.' } }));
            return;
        }
        setOrderAgainLoading(order.firestoreId);
        setOrderAgainMsg(prev => ({ ...prev, [order.firestoreId]: null }));
        try {
            for (const item of orderItems) {
                await addCartItem(currentUser.uid, {
                    name:           item.name          || item.type || '',
                    image:          item.image         || '',
                    type:           item.type          || '',
                    subcategory:    item.subcategory   || '',
                    color:          item.color         || '',
                    designShape:    item.designShape   || '',
                    printLocation:  item.printLocation || '',
                    quantities:     item.quantities    || {},
                    quantity:       item.quantity      || 0,
                    price:          item.price         || 0,
                    sizePrices:     item.sizePrices    || {},
                    colorSurcharge: item.colorSurcharge || 0,
                    note:           item.note          || '',
                    sizeBreakdown:  item.sizeBreakdown || '',
                    designImages:   item.designImages  || {},
                    teamName:       item.teamName      || '',
                    surname:        item.surname       || '',
                    number:         item.number        || '',
                });
            }
            setOrderAgainMsg(prev => ({ ...prev, [order.firestoreId]: { type:'success', text:'Items added to your cart.' } }));
            navigate('/cart');
        } catch (err) {
            setOrderAgainMsg(prev => ({ ...prev, [order.firestoreId]: { type:'error', text: err.message || 'Failed to add items to cart.' } }));
        } finally {
            setOrderAgainLoading(null);
        }
    }

    return (
        <>
            {!pageReady && <PageLoader message="Loading your orders…" />}

            {loading ? (
                <div className="purchase-history-content">
                    <div className="ph-loading">
                        <div className="ph-spinner"/>
                        <span>Loading your orders…</span>
                    </div>
                </div>
            ) : (
                <div className="purchase-history-content">
                    <h1>My Orders</h1>

                    {/* Tabs */}
                    <div className="order-tabs">
                        {TABS.map((t, i) => {
                            const count = countByTab(t);
                            return (
                                <div key={t.label} className={`order-tab ${activeTab===i?'active':''}`} onClick={() => setActiveTab(i)}>
                                    {t.label}
                                    {count > 0 && <span className="ph-tab-badge">{count}</span>}
                                </div>
                            );
                        })}
                    </div>

                    {/* Orders list */}
                    <div className="orders-list">
                        {filtered.length === 0 ? (
                            <div className="no-orders">No orders found for this status.</div>
                        ) : filtered.map(order => {
                            const sc          = { ...(STATUS_CONFIG[order.status] || { bg:'#eee', text:'#555', label:order.status||'—' }) };
                            if (order.status === ORDER_STATUS.TO_BE_DELIVERED) {
                                const method = (order.deliveryMethod || '').toLowerCase();
                                const isPickup = method.includes('pick-up') || method.includes('pickup');
                                if (!isPickup && method) {
                                    sc.label = 'Shipped';
                                }
                            }
                            const subtotal    = (order.orderItems||[]).reduce((s, item) => s + calcItemTotal(item), 0);
                            const grandTotal  = getOrderGrandTotal(order);
                            const hasDiscount = (order.discountAmount || 0) > 0;
                            const firstItem   = order.orderItems?.[0];
                            const isExpanded  = expandedId === order.firestoreId;
                            const isApproved  = order.status === ORDER_STATUS.APPROVED;
                            const stepIdx     = getStepIndex(order.status);
                            const isCancelled = order.status === ORDER_STATUS.CANCELLED;
                            const orderTitle  = buildOrderTitle(order.orderItems);
                            const extraCount  = (order.orderItems?.length || 1) - 1;

                            // Determine if first item is Polo/Jersey for card-level display
                            const firstIsPoloJersey = firstItem && NO_COLOR_SUBCATEGORIES.includes(firstItem.subcategory);

                            return (
                                <div key={order.firestoreId} className={`order-card ${isExpanded?'expanded':''}`}>

                                    {/* Card top row */}
                                    <div className="order-card-main" onClick={() => setExpandedId(isExpanded ? null : order.firestoreId)}>
                                        {firstItem?.image && (
                                            <img src={firstItem.image} alt="Order" className="order-image" />
                                        )}

                                        <div className="order-details">
                                            <div className="order-title">
                                                {orderTitle}
                                                {extraCount > 0 && (
                                                    <span className="order-multi-badge">+{extraCount} more</span>
                                                )}
                                            </div>
                                            {/* Only show color/print for T-Shirt items */}
                                            {firstItem?.color && !firstIsPoloJersey && (
                                                <div className="order-meta" style={{ textTransform:'capitalize' }}>
                                                    Color: {firstItem.color}
                                                    {firstItem?.printLocation && ` · ${firstItem.printLocation}`}
                                                </div>
                                            )}
                                            <div className="order-meta">Ordered on {formatDate(order.date)}</div>
                                            {order.paymentMethod && (
                                                <div className="order-meta">Payment: {order.paymentMethod}</div>
                                            )}
                                        </div>

                                        <div className="order-actions">
                                            <div className="order-price">
                                                {hasDiscount && (
                                                    <span className="ph-original-price">₱ {subtotal.toLocaleString()}</span>
                                                )}
                                                {grandTotal === 0
                                                    ? <span className="ph-free-label">FREE</span>
                                                    : `₱ ${grandTotal.toLocaleString()}`
                                                }
                                            </div>
                                            {order.firstOrderDiscount && (
                                                <div className="ph-first-order-tag">🎉 First Order Discount</div>
                                            )}
                                            <div className="order-status" style={{ background:sc.bg, color:sc.text }}>
                                                {sc.label}
                                            </div>
                                            {[ORDER_STATUS.IN_PRODUCTION,'in production','processing',ORDER_STATUS.TO_BE_DELIVERED,'to be delivered'].includes(order.status) && order.estimatedDeliveryDate && (
                                                <div className="order-estimated-date" style={{ background:'#e0ffe5', color:'#2d7a3a', padding:'4px 8px', borderRadius:'4px', fontSize:'12px', fontWeight:'500' }}>
                                                    Est. {formatDate(order.estimatedDeliveryDate)}
                                                </div>
                                            )}
                                            <div className="order-id">{order.orderId}</div>
                                            <svg className={`ph-expand-icon ${isExpanded?'open':''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <polyline points="6 9 12 15 18 9"/>
                                            </svg>
                                        </div>
                                    </div>

                                    {/* Approved CTA */}
                                    {isApproved && (
                                        <div className="ph-checkout-cta">
                                            <div className="ph-cta-text">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1a5faa" strokeWidth="2">
                                                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                                                </svg>
                                                Your order has been approved! Proceed to checkout to complete your order.
                                                {order.expiresAt && (() => {
                                                    const diff = new Date(order.expiresAt).getTime() - Date.now();
                                                    if (diff <= 0) return <span className="ph-expiry expired"> (Expired)</span>;
                                                    const hrs  = Math.floor(diff / 3600000);
                                                    const mins = Math.floor((diff % 3600000) / 60000);
                                                    return <span className="ph-expiry"> · {hrs}h {mins}m remaining</span>;
                                                })()}
                                            </div>
                                            <button className="ph-checkout-btn"
                                                onClick={e => { e.stopPropagation(); navigate('/checkout', { state:{ orderId:order.orderId } }); }}>
                                                Proceed to Checkout →
                                            </button>
                                        </div>
                                    )}

                                    {/* Expanded body */}
                                    {isExpanded && (
                                        <div className="ph-expanded-body">
                                            {!isCancelled && stepIdx >= 0 && (
                                                <div className="ph-progress-steps">
                                                    {STATUS_STEPS.map((step, i) => {
                                                        const isLastStep = i === STATUS_STEPS.length - 1;
                                                        const done    = i < stepIdx || (isLastStep && i === stepIdx);
                                                        const current = i === stepIdx && !isLastStep;
                                                        
                                                        let label = step.label;
                                                        if (label === 'Ready for Pickup') {
                                                            const method = (order.deliveryMethod || '').toLowerCase();
                                                            const isPickup = method.includes('pick-up') || method.includes('pickup');
                                                            if (!isPickup && method) {
                                                                label = 'Shipped';
                                                            }
                                                        }

                                                        return (
                                                            <div key={i} className="ph-step">
                                                                <div className={`ph-step-dot ${done?'done':current?'current':''}`}>
                                                                    {done && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                                                                </div>
                                                                <span className={`ph-step-label ${current?'current':done?'done':''}`}>{label}</span>
                                                                {i < STATUS_STEPS.length - 1 && <div className={`ph-step-line ${done?'done':''}`}/>}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}

                                            {isCancelled && order.cancellationReason && (
                                                <div className="ph-cancel-reason">
                                                    <strong>Cancellation reason:</strong> {order.cancellationReason}
                                                </div>
                                            )}

                                            {/* Items */}
                                            <div className="ph-items-grid">
                                                {(order.orderItems || []).map((item, idx) => {
                                                    const isPoloJersey   = NO_COLOR_SUBCATEGORIES.includes(item.subcategory);
                                                    const isJersey       = item.subcategory === 'Jersey';
                                                    const isGeneric      = isGenericProduct(item);
                                                    const isTShirt       = !isPoloJersey && !isGeneric;
                                                    const colorSurcharge = Number(item.colorSurcharge || 0);
                                                    const itemTotal      = calcItemTotal(item);

                                                    // Build per-size qty string
                                                    const sizeBreakdown = buildSizeBreakdown(item.quantities);
                                                    const totalQty      = Object.values(item.quantities || {}).reduce((s, q) => s + (q || 0), 0);

                                                    return (
                                                        <div key={item.id || idx} className="ph-item-card">
                                                            {item.image && <img src={item.image} alt={item.name} className="ph-item-thumb" />}
                                                            <div className="ph-item-info">
                                                                <h4>{item.type || item.name || `Item #${idx + 1}`}</h4>

                                                                {/* T-Shirt: Color, Design Shape, Print Location */}
                                                                {isTShirt && (
                                                                    <>
                                                                        {item.color && (
                                                                            <p>
                                                                                <strong>Color:</strong> {item.color}
                                                                                {colorSurcharge > 0 && (
                                                                                    <span style={{ marginLeft:5, fontSize:11, color:'#888' }}>
                                                                                        (+₱{colorSurcharge}/pc)
                                                                                    </span>
                                                                                )}
                                                                            </p>
                                                                        )}
                                                                        {item.designShape && (
                                                                            <p><strong>Design Shape:</strong> {item.designShape}</p>
                                                                        )}
                                                                        {item.printLocation && (
                                                                            <p><strong>Print Location:</strong> {item.printLocation}</p>
                                                                        )}
                                                                    </>
                                                                )}

                                                                {/* Jersey: Team Member, Surname, Number — no Color/Print Location */}
                                                                {isJersey && (
                                                                    <>
                                                                        {item.teamName && <p><strong>Team Member:</strong> {item.teamName}</p>}
                                                                        {item.surname  && <p><strong>Surname:</strong> {item.surname}</p>}
                                                                        {item.number   && <p><strong>Number:</strong> {item.number}</p>}
                                                                    </>
                                                                )}

                                                                {/* Generic / Non-clothing products */}
                                                                {isGeneric && (
                                                                    <>
                                                                        {item.category && <p><strong>Category:</strong> {item.category}</p>}
                                                                        {item.subcategory && <p><strong>Subcategory:</strong> {item.subcategory}</p>}
                                                                        {item.selectedAttributes && Object.keys(item.selectedAttributes).length > 0 && (
                                                                            Object.entries(item.selectedAttributes).map(([key, val]) => (
                                                                                <p key={key}><strong>{key}:</strong> {typeof val === 'object' && val !== null ? (val.value || val.label || JSON.stringify(val)) : String(val)}</p>
                                                                            ))
                                                                        )}
                                                                    </>
                                                                )}

                                                                {/* Qty: show per-size breakdown */}
                                                                <p>
                                                                    <strong>Qty:</strong>{' '}
                                                                    {isGeneric
                                                                        ? `${Number(item.quantity) || 0} pcs`
                                                                        : sizeBreakdown
                                                                            ? <span style={{ fontSize:12 }}>{sizeBreakdown} ({totalQty} pcs total)</span>
                                                                            : `${totalQty} pcs`
                                                                    }
                                                                </p>

                                                                <p className="ph-item-total">₱ {itemTotal.toLocaleString()}</p>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                            {/* Order total breakdown */}
                                            <div className="ph-order-total-breakdown">
                                                {hasDiscount && (
                                                    <>
                                                        <div className="ph-total-row">
                                                            <span>Subtotal</span>
                                                            <span>₱ {subtotal.toLocaleString()}</span>
                                                        </div>
                                                        {(order.deliveryFee || 0) > 0 && (
                                                            <div className="ph-total-row">
                                                                <span>Delivery Fee</span>
                                                                <span>₱ {order.deliveryFee.toLocaleString()}</span>
                                                            </div>
                                                        )}
                                                        <div className="ph-total-row ph-total-discount">
                                                            <span>
                                                                {order.firstOrderDiscount ? '🎉 First Order Discount' : `Promo (${order.promoCode || ''})`}
                                                            </span>
                                                            <span>−₱ {order.discountAmount.toLocaleString()}</span>
                                                        </div>
                                                    </>
                                                )}
                                                <div className="ph-order-total">
                                                    <span>Order Total</span>
                                                    <strong>
                                                        {grandTotal === 0
                                                            ? <span className="ph-free-label">FREE</span>
                                                            : `₱ ${grandTotal.toLocaleString()}`
                                                        }
                                                    </strong>
                                                </div>
                                            </div>

                                            <div className="ph-order-actions">
                                                <button className="ph-order-again-btn"
                                                    onClick={e => { e.stopPropagation(); handleOrderAgain(order); }}
                                                    disabled={orderAgainLoading === order.firestoreId}>
                                                    {orderAgainLoading === order.firestoreId ? 'Adding to Cart…' : 'Order Again'}
                                                </button>
                                                {orderAgainMsg[order.firestoreId] && (
                                                    <span className={`ph-order-again-msg ${orderAgainMsg[order.firestoreId].type}`}>
                                                        {orderAgainMsg[order.firestoreId].text}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </>
    );
}

export default PurchaseHistory;